import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wrench, Cog, Euro, MapPin, ArrowRight, CheckCircle, Package, Users } from 'lucide-react';
import { getListings } from '@/lib/api';
import { setPageSeo, setJsonLd } from '@/lib/seo';

/**
 * SEO-optimized landing page for a single marketplace vertical.
 * Rendered at `/ersatzteile` (spare_parts) and `/druckmaschinen` (machines).
 *
 * Injects:
 *   - H1 + meta description + OG tags via setPageSeo
 *   - JSON-LD `ItemList` with the first 12 listings (nested `Product` items with price + availability)
 */
const META = {
  spare_parts: {
    vertical: 'spare_parts',
    slug: 'ersatzteile',
    title: 'Ersatzteile für Druckmaschinen – Gebraucht & Refurbished | Surplify.de',
    h1: 'Ersatzteile & Module für Druckmaschinen',
    subtitle: 'Der B2B-Marktplatz für geprüfte Gebraucht- und Refurbished-Ersatzteile — von Heidelberg, KBA, Komori, Bobst, Nilpeter, Mark Andy, Gallus und mehr.',
    description: 'Tausende gebrauchte und refurbished Ersatzteile für Bogen-/Rollenoffset, Flexo-, Digital- und Schmalbahndruck. Mit Hersteller- & Modell-Kompatibilität, Zustand, Garantie-Optionen.',
    icon: Wrench,
    iconColor: 'text-orange-500',
    accent: 'from-orange-500 to-amber-500',
    cta: 'Alle Ersatzteile durchsuchen',
    ctaHref: '/listings?vertical=spare_parts',
    perks: [
      'Vollständige Hersteller- & Modell-Kompatibilität',
      'Transparente Zustandsangabe (Neu, Refurbished, Gebraucht)',
      'OEM-Nummer und Teilenummer für eindeutige Identifikation',
      'Direkt-Chat mit geprüften Anbietern aus Europa',
    ],
    categories: [
      'Druckwerke & Farbwerke',
      'Antrieb & Mechanik',
      'Elektronik & Steuerung',
      'Anleger & Ausleger',
      'UV & Trocknung',
      'Schneiden & Finishing',
    ],
  },
  machines: {
    vertical: 'machines',
    slug: 'druckmaschinen',
    title: 'Gebrauchte Druckmaschinen kaufen – Bogenoffset, Flexo, Digital | Surplify.de',
    h1: 'Gebrauchte Druckmaschinen & komplette Produktionslinien',
    subtitle: 'Geprüfte Maschinen aus ganz Europa — Bogen- und Rollenoffset, Flexo, Digital, Tief- und Siebdruck, Finishing und Vorstufe.',
    description: 'Gebrauchte Druckmaschinen direkt vom Betreiber: mit Betriebsstunden, Maschinen-Specs, Seriennummer und Besichtigungstermin. Plus Finanzierungs- und Transport-Kontakte.',
    icon: Cog,
    iconColor: 'text-blue-500',
    accent: 'from-blue-600 to-cyan-500',
    cta: 'Alle Druckmaschinen durchsuchen',
    ctaHref: '/listings?vertical=machines',
    perks: [
      'Tech-Specs (Farbwerke, Bahnbreite, Geschwindigkeit, Substratarten)',
      'Vor-Ort-Besichtigung anfragbar (strukturierte Enquiry)',
      'Hersteller-Autocomplete: Heidelberg, manroland, KBA, Komori, Bobst …',
      'Direkter Kontakt zum Betreiber — kein Makler-Aufschlag',
    ],
    categories: [
      'Bogenoffsetdruck',
      'Rollenoffset',
      'Flexodruck',
      'Digitaldruck',
      'Tiefdruck',
      'Finishing & Weiterverarbeitung',
    ],
  },
};

export default function SeoLandingPage({ vertical }) {
  const meta = META[vertical];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getListings({ vertical: meta.vertical, limit: 12, page: 1 });
        if (!cancelled) setItems(res.data.items || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [meta.vertical]);

  useEffect(() => {
    const url = `https://surplify.de/${meta.slug}`;
    const cleanupSeo = setPageSeo({
      title: meta.title,
      description: meta.description,
      url,
      type: 'website',
    });
    const cleanupJsonLd = setJsonLd(`vertical-${meta.vertical}`, {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: meta.h1,
      description: meta.description,
      itemListOrder: 'https://schema.org/ItemListOrderDescending',
      numberOfItems: items.length,
      itemListElement: items.slice(0, 12).map((l, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://surplify.de/listing/${l.listing_id}`,
        item: {
          '@type': 'Product',
          name: l.title,
          image: l.photos?.[0] || undefined,
          brand: l.manufacturer_name || undefined,
          sku: l.part_number || l.listing_id,
          offers: l.price_eur != null ? {
            '@type': 'Offer',
            priceCurrency: l.currency || 'EUR',
            price: l.price_eur,
            availability: 'https://schema.org/InStock',
            url: `https://surplify.de/listing/${l.listing_id}`,
          } : undefined,
        },
      })),
    });
    return () => { cleanupSeo(); cleanupJsonLd(); };
  }, [items, meta]);

  const Icon = meta.icon;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main data-testid={`seo-landing-${meta.slug}`}>
        {/* Hero */}
        <section className={`bg-gradient-to-br ${meta.accent} text-white`}>
          <div className="container mx-auto px-4 py-16 max-w-6xl">
            <div className="flex items-center gap-2 mb-4">
              <Icon className="w-8 h-8" />
              <Badge variant="outline" className="bg-white/10 border-white/30 text-white">
                B2B-Marktplatz Surplify.de
              </Badge>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="seo-landing-h1">
              {meta.h1}
            </h1>
            <p className="text-lg max-w-3xl text-white/90 mb-8">{meta.subtitle}</p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100" asChild>
                <Link to={meta.ctaHref} data-testid="seo-landing-cta">
                  {meta.cta} <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10" asChild>
                <Link to="/register" data-testid="seo-landing-register-cta">Kostenlos registrieren</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Perks */}
        <section className="container mx-auto px-4 py-12 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {meta.perks.map((p, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-lg border bg-card">
                <CheckCircle className={`w-5 h-5 flex-none mt-0.5 ${meta.iconColor}`} />
                <p className="text-sm">{p}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Preview Listings */}
        <section className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold tracking-tight">Aktuelle Inserate</h2>
            <Button variant="ghost" asChild>
              <Link to={meta.ctaHref} className="text-sm">
                Alle ansehen <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </Button>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}><CardContent className="p-4 h-40 bg-muted/30 animate-pulse" /></Card>
              ))}
            </div>
          ) : items.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Aktuell sind keine Inserate online. Schauen Sie bald wieder vorbei!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map(l => (
                <Link key={l.listing_id} to={`/listing/${l.listing_id}`} data-testid={`seo-listing-${l.listing_id}`}>
                  <Card className="hover:shadow-md transition-shadow h-full">
                    <CardContent className="p-4 space-y-2">
                      {l.photos?.[0] && (
                        <img src={l.photos[0]} alt={l.title} className="w-full h-32 object-cover rounded" loading="lazy" />
                      )}
                      <h3 className="font-semibold line-clamp-2">{l.title}</h3>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {l.country || '—'}
                        </span>
                        {l.price_eur != null && (
                          <span className="font-semibold flex items-center gap-0.5">
                            <Euro className="w-3 h-3" /> {Number(l.price_eur).toLocaleString('de-DE')}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Categories */}
        <section className="container mx-auto px-4 py-12 max-w-6xl">
          <h2 className="text-2xl font-bold tracking-tight mb-6">Beliebte Kategorien</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {meta.categories.map((cat) => (
              <Link key={cat} to={`${meta.ctaHref}&search=${encodeURIComponent(cat)}`}>
                <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${meta.iconColor}`} />
                    <span className="text-sm font-medium">{cat}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        {/* Trust */}
        <section className="bg-muted/40 border-t">
          <div className="container mx-auto px-4 py-12 max-w-6xl text-center">
            <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <h2 className="text-2xl font-bold tracking-tight mb-2">Einfach, transparent, direkt</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
              Surplify verbindet Druckbetriebe, Zulieferer und Händler in ganz Europa. Registrieren Sie sich kostenlos,
              laden Sie Ihren Bestand hoch oder finden Sie, was Sie suchen.
            </p>
            <Button size="lg" asChild>
              <Link to={meta.ctaHref} data-testid="seo-landing-cta-bottom">{meta.cta}</Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
